// viewer.quality.js — the quality menu's rules (spec 2026-09-17 §5).
//
// Pure functions over hls.js Level objects ({ height, width, bitrate,
// videoCodec }) so the cap, the Max predicate and the labels can be tested
// without mounting the viewer. Shared by the web app and the HDD shell
// (the shell never has an hls instance, so it never calls these).

// Boundary between the 50 Mbps analyst rung and anything the operator
// meant as a manual-only "Max": at or under it caps auto mode, above it
// needs the one-time bandwidth confirm.
export const MAX_RUNG_BITRATE = 55_000_000;

// The last level (level order = master playlist order, ascending) at or
// under the cap. -1 when nothing qualifies (hls.js then leaves auto uncapped).
export function autoCapIndex(levels) {
  let cap = -1;
  levels.forEach((l, i) => {
    if (l.bitrate <= MAX_RUNG_BITRATE) cap = i;
  });
  return cap;
}

// The tallest level: what a freeze frame is taken from, whatever is playing.
// The first of equally tall levels, -1 for none. hls.js has already dropped
// levels this browser cannot decode, so this never picks H.265 on Firefox.
export function topLevelIndex(levels) {
  let top = -1;
  levels.forEach((l, i) => {
    if (top === -1 || l.height > levels[top].height) top = i;
  });
  return top;
}

export function isMaxLevel(level) {
  return level.bitrate > MAX_RUNG_BITRATE;
}

// hls.js exposes the RFC 6381 codec string on level.videoCodec.
export function isHevcLevel(level) {
  const codec = String(level.videoCodec ?? "").toLowerCase();
  return codec.startsWith("hvc1") || codec.startsWith("hev1");
}

// Same rule as encode.native_label: 3840/4096 are "4K", 7680 is "8K", the
// rest is the width in thousands to one decimal.
export function rasterLabel(width) {
  if (width === 3840 || width === 4096) return "4K";
  if (width === 7680) return "8K";
  return `${(width / 1000).toFixed(1)}K`;
}

// An H.265 level reads "4.6K H.265" (by width); a 4K H.264 level reads
// "4K H.264" so the two are told apart by codec, not by "2160p" against
// "4.6K" (since 2026-09-18); sub-4K H.264 levels read "720p" / "1080p". When
// two H.264 levels share a height the rate tells them apart: "4K H.264
// 50 Mbps" / "4K H.264 80 Mbps", or "720p3" / "720p10" below 4K.
//
// The rate rounds to the nearest 10 Mbps above 4K and the nearest 1 below:
// hls.js reports each level's measured peak bandwidth (Shaka's BANDWIDTH),
// which runs a little over the rung's nominal target (52 Mbps for the "50"
// rung, 84 for the "80" one) — rounding to the ten recovers the rung name.
const UHD_HEIGHT = 2160;

// RFC 6381 "avc1.PPCCLL": PP is the profile_idc. 110 = High 10, 122 = High
// 4:2:2, 244 = High 4:4:4 Predictive — the 10-bit H.264 profiles a publish
// with --10-bit leaves (since 2026-09-22). The H.265 rung is 10-bit always
// and its label says H.265, which already tells the two apart.
const TEN_BIT_H264_PROFILES = new Set(["6e", "7a", "f4"]);
export function isTenBitH264Level(level) {
  const codec = String(level.videoCodec ?? "").toLowerCase();
  const m = /^avc[13]\.([0-9a-f]{2})/.exec(codec);
  return m !== null && TEN_BIT_H264_PROFILES.has(m[1]);
}

export function levelLabel(level, levels) {
  if (isHevcLevel(level)) return `${rasterLabel(level.width)} H.265`;
  const uhd = level.height >= UHD_HEIGHT;
  const sameHeight = levels.filter((l) => !isHevcLevel(l) && l.height === level.height);
  const depth = isTenBitH264Level(level) ? " 10-bit" : "";
  if (uhd) {
    const base = `${rasterLabel(level.width)} H.264`;
    if (sameHeight.length > 1) {
      return `${base} ${Math.round(level.bitrate / 1e7) * 10} Mbps${depth}`;
    }
    return `${base}${depth}`;
  }
  if (sameHeight.length > 1) return `${level.height}p${Math.round(level.bitrate / 1e6)}${depth}`;
  return `${level.height}p${depth}`;
}

// The short name of a level for the badge beside the menu: "720p", "1080p",
// "4K", "4.6K". No codec and no rate; the menu's own entries carry those.
export function shortLevelLabel(level) {
  if (isHevcLevel(level) || level.height >= UHD_HEIGHT) return rasterLabel(level.width);
  return `${level.height}p`;
}

// What the badge beside the menu shows: the playing level's short name
// while Auto is choosing, nothing once the reviewer has picked a level by
// hand (the menu then says it) or before any level has started.
export function autoLevelBadge(levels, levelIndex, autoLevelEnabled) {
  if (!autoLevelEnabled || levelIndex < 0 || levelIndex >= levels.length) return "";
  return shortLevelLabel(levels[levelIndex]);
}

// Whether `url` is on the Worker's own origin, so it may carry the team's
// bearer token. Compared as parsed origins, never as a string prefix:
// "https://worker.example.evil.test/" starts with "https://worker.example".
// A relative URL, an unparsable one or a missing origin is never a match.
export function isWorkerUrl(url, workerOrigin) {
  if (typeof url !== "string" || typeof workerOrigin !== "string" || workerOrigin === "") {
    return false;
  }
  try {
    const origin = new URL(workerOrigin).origin;
    // file:, data: and scheme-less strings all parse to the opaque origin
    // "null", which would match each other.
    return origin !== "null" && new URL(url).origin === origin;
  } catch {
    return false;
  }
}
